import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useReveal from "@/hooks/useReveal";
import {
  Upload, FileText, Trash2, Download, X, Loader2, Plus,
  Briefcase, Sparkles, ArrowUpRight, Mail,
} from "lucide-react";

const CATEGORIES = ["Offres d'emploi", "Nos valeurs", "Avantages", "Processus de recrutement", "Général"];
const ACCEPT_MIME = ".pdf,.doc,.docx,.xls,.xlsx,.zip,.png,.jpg,.jpeg,.txt,.csv";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}
function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

const UploadModal = ({ onClose, onUploaded }) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Offres d'emploi");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Sélectionnez un fichier");
    if (title.trim().length < 2) return toast.error("Titre trop court");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title.trim());
    fd.append("category", category);
    fd.append("board", "recruitment");
    if (description) fd.append("description", description);
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Document « ${data.title} » publié`);
      onUploaded(data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec du téléchargement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="recruit-upload-modal"
      className="fixed inset-0 z-[70] bg-teal-dark/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg border border-ink-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200">
          <div>
            <p className="eyebrow text-teal-accent">Recrutement</p>
            <h3 className="font-serif text-2xl text-teal-dark mt-1">Nouveau document</h3>
          </div>
          <button type="button" onClick={onClose} className="text-ink-500 hover:text-teal-dark">
            <X size={22} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Titre *</span>
            <input
              data-testid="recruit-upload-title"
              value={title} onChange={(e) => setTitle(e.target.value)} required minLength={2}
              placeholder="Ex : Fiche de poste — Collaborateur comptable"
              className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
            />
          </label>
          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Catégorie</span>
            <select
              data-testid="recruit-upload-category"
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-ink-200 px-4 py-3 bg-white focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20"
            >
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="eyebrow text-ink-500 block mb-2">Description (facultative)</span>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-ink-200 px-4 py-3 focus:border-teal-accent focus:outline-none focus:ring-2 focus:ring-teal-accent/20 resize-none"
            />
          </label>
          <div>
            <span className="eyebrow text-ink-500 block mb-2">Fichier * (max 15 Mo)</span>
            <input ref={inputRef} data-testid="recruit-upload-file" type="file" accept={ACCEPT_MIME}
              onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
            <button type="button" onClick={() => inputRef.current?.click()}
              className="w-full border border-dashed border-teal-dark/40 hover:border-teal-accent p-6 text-left transition-colors">
              {file ? (
                <div className="flex items-center gap-3">
                  <FileText size={22} className="text-teal-accent" />
                  <div>
                    <p className="text-ink-800 text-sm">{file.name}</p>
                    <p className="text-xs text-ink-500">{formatSize(file.size)}</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-ink-500">
                  <Upload size={22} className="text-teal-accent" />
                  <span className="text-sm">Cliquer pour sélectionner un fichier</span>
                </div>
              )}
            </button>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-ink-200 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-sm text-ink-500 hover:text-teal-dark px-4">Annuler</button>
          <button type="submit" data-testid="recruit-upload-submit" disabled={loading}
            className="btn-primary text-sm disabled:opacity-60">
            {loading ? (<><Loader2 size={16} className="animate-spin" /> Envoi…</>) : (<><Upload size={16} /> Publier</>)}
          </button>
        </div>
      </form>
    </div>
  );
};

const RecruitmentPage = () => {
  const ref = useReveal();
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [docs, setDocs] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [showUpload, setShowUpload] = useState(false);

  const load = () => {
    api.get("/recruitment/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  };

  useEffect(() => { window.scrollTo(0, 0); load(); }, []);

  const categories = useMemo(() => {
    const s = new Set(["Tous"]);
    (docs || []).forEach((d) => s.add(d.category || "Général"));
    return Array.from(s);
  }, [docs]);
  const visible = useMemo(() => {
    if (!docs) return [];
    if (filter === "Tous") return docs;
    return docs.filter((d) => (d.category || "Général") === filter);
  }, [docs, filter]);

  const doDownload = async (doc) => {
    try {
      const res = await api.get(`/newsletter/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || doc.title;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    }
  };

  const doDelete = async (doc) => {
    if (!window.confirm(`Supprimer « ${doc.title} » ?`)) return;
    try {
      await api.delete(`/newsletter/documents/${doc.id}`);
      toast.success("Document supprimé");
      setDocs((d) => d.filter((x) => x.id !== doc.id));
    } catch {
      toast.error("Suppression impossible");
    }
  };

  return (
    <div ref={ref} data-testid="recruitment-page">
      {/* HERO */}
      <section className="bg-teal-dark text-white pt-24 pb-20 md:pt-32 md:pb-24 grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <p className="eyebrow text-teal-l3">Rejoignez-nous</p>
            <h1 className="mt-4 font-serif text-5xl md:text-7xl leading-[1.02] font-light">
              Construisez votre carrière au <em className="text-teal-l3">cabinet Ducrot</em>.
            </h1>
            <p className="mt-8 max-w-2xl text-teal-l3 text-lg leading-relaxed">
              Un cabinet à taille humaine, une équipe pluridisciplinaire, des missions variées
              auprès d'entrepreneurs, TPE et PME. Découvrez nos opportunités et notre culture.
            </p>
          </div>
          <div className="lg:col-span-4 reveal">
            <div className="border border-white/25 p-6 backdrop-blur-sm bg-white/5 space-y-4">
              <div className="flex items-center gap-3">
                <Sparkles size={20} className="text-teal-l3" />
                <span className="text-sm">Formation continue financée</span>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase size={20} className="text-teal-l3" />
                <span className="text-sm">Missions variées, portefeuille dédié</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={20} className="text-teal-l3" />
                <span className="text-sm">Candidature spontanée bienvenue</span>
              </div>
              <Link to="/contact" className="mt-2 inline-flex items-center gap-2 text-sm border-b border-white pb-0.5 hover:text-teal-l3">
                Nous écrire <ArrowUpRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DOCS */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-start justify-between gap-4 flex-wrap reveal">
            <div>
              <p className="eyebrow text-teal-accent">Documents utiles</p>
              <h2 className="mt-3 font-serif text-4xl md:text-5xl text-teal-dark leading-[1.05]">
                Tout savoir avant de postuler.
              </h2>
              <p className="mt-4 text-ink-500 max-w-2xl leading-relaxed">
                Fiches de poste, présentation du cabinet, processus de recrutement, avantages… Consultez et téléchargez librement les documents mis à disposition.
              </p>
            </div>
            {isAdmin && (
              <button
                data-testid="recruit-upload-btn"
                onClick={() => setShowUpload(true)}
                className="btn-primary text-sm"
              >
                <Plus size={16} /> Ajouter un document
              </button>
            )}
          </div>

          <div className="mt-12 border border-ink-200 bg-white" data-testid="recruit-list">
            {docs === null ? (
              <div className="p-10 text-ink-500 text-sm">Chargement…</div>
            ) : docs.length === 0 ? (
              <div className="p-16 text-center">
                <FileText size={40} className="mx-auto text-teal-accent" />
                <p className="font-serif text-2xl text-teal-dark mt-4">Aucune publication pour le moment</p>
                <p className="text-ink-500 text-sm mt-2">
                  {isAdmin
                    ? "Publiez la première fiche de poste ci-dessus."
                    : "Revenez bientôt : nos annonces seront publiées ici."}
                </p>
                <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm text-teal-dark border border-teal-dark px-5 py-2 hover:bg-teal-l1">
                  Candidature spontanée <ArrowUpRight size={14} />
                </Link>
              </div>
            ) : (
              <>
                {categories.length > 2 && (
                  <div className="px-6 pt-5 flex flex-wrap gap-2">
                    {categories.map((c) => (
                      <button key={c} onClick={() => setFilter(c)}
                        className={`text-xs eyebrow px-3 py-1.5 border transition-colors ${
                          filter === c ? "bg-teal-dark text-white border-teal-dark"
                                       : "border-ink-200 text-ink-500 hover:border-teal-dark hover:text-teal-dark"
                        }`}>
                        {c}
                      </button>
                    ))}
                  </div>
                )}
                <ul className="divide-y divide-ink-200">
                  {visible.map((doc) => (
                    <li key={doc.id} data-testid={`recruit-doc-${doc.id}`}
                      className="p-6 flex flex-col md:flex-row md:items-center gap-4 hover:bg-teal-l1/40 transition-colors">
                      <div className="bg-teal-l1 p-3 shrink-0"><FileText size={22} className="text-teal-dark" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-baseline gap-3">
                          <h4 className="font-serif text-xl text-teal-dark leading-tight truncate">{doc.title}</h4>
                          <span className="text-xs eyebrow text-teal-accent bg-teal-l1 px-2 py-0.5">{doc.category || "Général"}</span>
                        </div>
                        {doc.description && <p className="text-sm text-ink-500 mt-1">{doc.description}</p>}
                        <p className="text-xs text-ink-500 mt-2">
                          {doc.original_filename} · {formatSize(doc.size)} · Publié le {formatDate(doc.created_at)}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => doDownload(doc)}
                          data-testid={`recruit-download-${doc.id}`}
                          className="inline-flex items-center gap-2 text-sm text-teal-dark border border-teal-dark px-3 py-2 hover:bg-teal-l1">
                          <Download size={14} /> Télécharger
                        </button>
                        {isAdmin && (
                          <button onClick={() => doDelete(doc)}
                            data-testid={`recruit-delete-${doc.id}`}
                            className="inline-flex items-center gap-2 text-sm text-red-700 border border-red-200 px-3 py-2 hover:bg-red-50">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-teal-l1 py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <p className="eyebrow text-teal-accent">Envie de nous rejoindre ?</p>
            <h3 className="mt-3 font-serif text-3xl md:text-4xl text-teal-dark">
              Envoyez-nous votre CV en quelques clics.
            </h3>
          </div>
          <Link to="/contact" className="btn-primary" data-testid="recruit-contact-cta">
            Postuler <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUploaded={(doc) => setDocs((d) => [doc, ...(d || [])])} />}
    </div>
  );
};

export default RecruitmentPage;
